import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { take } from 'rxjs/operators';
import { getSupportedMimeType } from '@raptor-fe/core';

export interface MediaFile {
    filename: string;
    full: Photo | string;
    mimeType: string;
    src: string;
    blob?: Blob;
}

/**
 * Downscale and JPEG re-encode applied to a photo before it is attached. `maxEdge` bounds the
 * longer side in pixels and the aspect ratio is kept; `quality` is JPEG quality from 0 to 100.
 */
export interface ImageCompression {
    maxEdge: number;
    quality: number;
}

/**
 * Preferred camera frame size in pixels. The browser treats it as a target, so a camera that cannot
 * reach it still opens at the closest size it supports.
 */
export interface StreamResolution {
    width: number;
    height: number;
}

/**
 * Recorded-video bitrates in bits per second. Tech Check uploads are capped at about 2.5 Mbps; a recording
 * left at the WebView's default lands just over that and the federation re-encodes all of it while the
 * advisor waits at transfer. Recording under the cap lets the federation upload the file as it is.
 */
export const RecordedVideoBitsPerSecond = 2_000_000;
export const RecordedAudioBitsPerSecond = 96_000;

@Injectable({ providedIn: 'root' })
export class PhotoService {
    photo: Subject<MediaFile> = new Subject();
    recordedVideo: Subject<MediaFile> = new Subject();
    uploadedMedia: Subject<MediaFile> = new Subject();

    photos: MediaFile[] = [];
    videos: MediaFile[] = [];
    private isRecording = false;
    private captureElement!: HTMLVideoElement;
    private mediaRecorder!: MediaRecorder;
    private stream!: MediaStream;
    private torchOn = false;

    /**
     * Captures one photo, emits it on `photo` and also returns it, so a caller taking several
     * photos in a row can await each capture without juggling subscriptions. Without
     * `compression` the photo stays at full sensor resolution, which plate recognition relies on.
     */
    async takePhoto(source: CameraSource, allowEditing = false, compression?: ImageCompression): Promise<MediaFile> {
        const capturedPhoto = await Camera.getPhoto({
            resultType: CameraResultType.Uri,
            source: source,
            quality: compression?.quality ?? 100,
            width: compression?.maxEdge,
            height: compression?.maxEdge,
            allowEditing,
        });

        const webPath = capturedPhoto.webPath ?? '';
        const blob = await fetch(webPath).then((response) => response.blob());

        this.photos.unshift({
            filename: this.generateUniqueFilename() + '.' + (capturedPhoto.format || 'jpeg'),
            mimeType: blob.type || 'image/jpeg',
            full: capturedPhoto,
            src: webPath,
            blob,
        });

        this.photo.next(this.photos[0]);
        return this.photos[0];
    }

    /**
     * Initializes the video stream.
     * Forces the element to be muted and uses the front facing camera.
     *
     * @param element - The element to display the video feed to.
     * @param flipImage - Whether to flip the video feed.
     * @param frontCamera - Whether to use the front camera.
     * @param audio - Whether to include audio in the stream.
     * @param resolution - Preferred frame size. Stills grabbed from the stream need more pixels than the
     * browser's default stream size, while the video recorder keeps that default to hold file size down.
     */
    async initializeVideoStream(
        element: HTMLVideoElement,
        flipImage = false,
        frontCamera = false,
        audio = true,
        resolution?: StreamResolution,
    ): Promise<void> {
        this.captureElement = element;
        this.captureElement.muted = true;
        this.torchOn = false;
        this.stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: frontCamera ? 'user' : 'environment',
                ...(resolution ? { width: { ideal: resolution.width }, height: { ideal: resolution.height } } : {}),
            },
            audio: audio,
        });

        this.captureElement.srcObject = this.stream;

        // Flip the video feed if required
        this.captureElement.style.transform = flipImage ? 'scaleX(-1)' : 'scaleX(1)';
    }

    /**
     * Whether the live camera can drive its flash as a torch. The recorder runs on a WebView
     * stream rather than the native camera app, so the flash is only reachable through the track.
     */
    isTorchSupported(): boolean {
        const track = this.stream?.getVideoTracks()[0];
        const capabilities = track?.getCapabilities?.() as (MediaTrackCapabilities & { torch?: boolean }) | undefined;
        return capabilities?.torch === true;
    }

    /**
     * Switches the camera flash on or off for the live stream. Stopping the stream turns it off.
     *
     * @param on - Whether the torch should be lit.
     */
    async setTorch(on: boolean): Promise<void> {
        const track = this.stream?.getVideoTracks()[0];
        if (!track) {
            return;
        }

        await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
        this.torchOn = on;
    }

    recordVideo(): void {
        if (!this.isRecording) {
            this.isRecording = true;

            const supportedMimeType = getSupportedMimeType();
            const options: MediaRecorderOptions = {
                mimeType: supportedMimeType,
                videoBitsPerSecond: RecordedVideoBitsPerSecond,
                audioBitsPerSecond: RecordedAudioBitsPerSecond,
            };
            this.mediaRecorder = new MediaRecorder(this.stream, options);
            // chunks are a subject because there is a chance that ondataavailable is emitted after the recording/
            const chunksSubject: ReplaySubject<BlobPart[]> = new ReplaySubject<BlobPart[]>();
            const chunks: BlobPart[] = [];

            this.mediaRecorder.start();

            if (this.torchOn) {
                this.setTorch(true).catch((error) => console.error('Error keeping torch on:', error));
            }

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    chunks.push(event.data);
                    chunksSubject.next(chunks);
                }
            };

            this.mediaRecorder.onstop = async () => {
                this.onStopRecordingCallback(chunksSubject.asObservable(), supportedMimeType);
            };
        }
    }

    stopRecord(): void {
        if (this.stream) {
            this.stream.getTracks().forEach((track) => {
                track.stop();
                track.enabled = false;
                if (this.mediaRecorder?.stream) {
                    if (this.mediaRecorder.stream.active) {
                        this.mediaRecorder.stream.removeTrack(track);
                        this.mediaRecorder.stop();
                    }
                }
            });
        }

        this.isRecording = false;
        this.torchOn = false;
    }

    /**
     * Stops the live camera without touching the video recorder, for screens that only take stills.
     * Stopping the track also puts the torch out.
     */
    stopStream(): void {
        this.stream?.getTracks().forEach((track) => track.stop());
        if (this.captureElement) {
            this.captureElement.srcObject = null;
        }
        this.torchOn = false;
    }

    /**
     * Takes a still from the live stream so several photos can be taken without leaving the app.
     * When the preview already has more pixels than the compressed photo keeps, the preview frame is
     * used directly: ImageCapture reconfigures the camera for a full-sensor shot that stalls the
     * preview for seconds on Android tablets, only for that shot to be shrunk straight after.
     * Otherwise ImageCapture asks the camera for a real photo where the web view supports it, and the
     * preview frame is the fallback. Nothing is emitted on the photo subjects because the caller
     * holds the photos until the technician is done.
     */
    async capturePhotoFromStream(compression?: ImageCompression): Promise<MediaFile> {
        let blob = this.previewCoversCompression(compression) ? undefined : await this.takeStreamPhoto();
        if (blob && compression) {
            blob = await this.compressImage(blob, compression);
        }
        blob ??= await this.grabPreviewFrame(compression);

        return {
            filename: this.generateUniqueFilename() + '.jpeg',
            full: '',
            mimeType: 'image/jpeg',
            src: URL.createObjectURL(blob),
            blob,
        };
    }

    /**
     * Opens the device photo and video picker, emits each picked file on `uploadedMedia` and
     * returns them all. The picker only supports a limit of one or unlimited on Android and web,
     * so callers that allow several files apply their own cap to the result. With `compression`,
     * picked photos are downscaled and re-encoded as JPEG; videos are never touched.
     */
    async selectMediaFromGallery(multiple = false, compression?: ImageCompression): Promise<MediaFile[]> {
        const result = await FilePicker.pickMedia({ limit: multiple ? 0 : 1 });
        const files: MediaFile[] = [];

        for (const picked of result.files) {
            let mimeType = picked.mimeType;
            let blob: Blob = picked.blob ?? (await (await fetch(Capacitor.convertFileSrc(picked.path as string))).blob());
            if (compression && mimeType.startsWith('image/')) {
                const compressed = await this.compressImage(blob, compression);
                if (compressed !== blob) {
                    blob = compressed;
                    mimeType = 'image/jpeg';
                }
            }
            const file: MediaFile = {
                filename: this.generateUniqueFilename(),
                full: '',
                mimeType: mimeType,
                src: URL.createObjectURL(blob),
                blob,
            };

            if (mimeType.split('/')[0] == 'image') {
                file.filename += '.' + file.mimeType.split('/')[1];
                this.photos.unshift(file);
            } else {
                file.filename += '.' + file.mimeType.split('/')[1].split(';')[0].trim();
                this.videos.unshift(file);
            }

            this.uploadedMedia.next(file);
            files.push(file);
        }

        return files;
    }

    /**
     * Callback method for mediarecorder.onstop.
     * Keeps the recorded video as a Blob and exposes it for multipart upload,
     * using an object URL for preview.
     */
    private onStopRecordingCallback(chunks$: Observable<BlobPart[]>, supportedMimeType: string): void {
        chunks$.pipe(take(1)).subscribe((chunks) => {
            const videoBuffer = new Blob(chunks, { type: supportedMimeType });
            const video: MediaFile = {
                filename: this.generateUniqueFilename() + '.' + supportedMimeType.split('/')[1].split(';')[0].trim(),
                full: '',
                mimeType: supportedMimeType,
                src: URL.createObjectURL(videoBuffer),
                blob: videoBuffer,
            };

            this.videos.unshift(video);
            this.recordedVideo.next(video);
        });
    }

    /**
     * Some Android cameras put the torch out while they take a photo, so a lit torch is lit again
     * afterwards. Returns undefined when ImageCapture is missing or fails, so the caller falls back.
     */
    private async takeStreamPhoto(): Promise<Blob | undefined> {
        const imageCaptureType = (globalThis as { ImageCapture?: typeof ImageCapture }).ImageCapture;
        const track = this.stream?.getVideoTracks()[0];
        if (!imageCaptureType || !track) {
            return undefined;
        }

        try {
            const blob = await new imageCaptureType(track).takePhoto();
            if (this.torchOn) {
                await this.setTorch(true).catch((error) => console.error('Error relighting torch:', error));
            }
            return blob;
        } catch (error) {
            console.error('Error taking photo with ImageCapture:', error);
            return undefined;
        }
    }

    /** Whether a preview frame alone is at least as sharp as the compressed photo would be. */
    private previewCoversCompression(compression?: ImageCompression): boolean {
        const video = this.captureElement;
        return !!compression && !!video && Math.max(video.videoWidth, video.videoHeight) >= compression.maxEdge;
    }

    /**
     * Draws the current preview frame to a canvas, downscaled to the compression's max edge so the
     * frame is encoded once rather than encoded, decoded and encoded again. It is only as sharp as
     * the stream, which is why the photo screen asks for a higher stream resolution.
     */
    private async grabPreviewFrame(compression?: ImageCompression): Promise<Blob> {
        const video = this.captureElement;
        if (!video?.videoWidth || !video.videoHeight) {
            throw new Error('The camera is not ready');
        }

        const scale = compression ? Math.min(1, compression.maxEdge / Math.max(video.videoWidth, video.videoHeight)) : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', (compression?.quality ?? 92) / 100));
        if (!blob) {
            throw new Error('The camera frame could not be encoded');
        }
        return blob;
    }

    /**
     * Downscales an image so its longer side fits `maxEdge` and re-encodes it as JPEG, honouring
     * the EXIF orientation. An image the web view cannot decode (some HEIC on older Android) is
     * returned unchanged, so a pick is never lost just because it could not be shrunk.
     */
    private async compressImage(blob: Blob, compression: ImageCompression): Promise<Blob> {
        try {
            const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
            const scale = Math.min(1, compression.maxEdge / Math.max(bitmap.width, bitmap.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(bitmap.width * scale);
            canvas.height = Math.round(bitmap.height * scale);
            canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
            bitmap.close();

            const compressed = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', compression.quality / 100));
            return compressed && compressed.size < blob.size ? compressed : blob;
        } catch (error) {
            console.error('Error compressing image:', error);
            return blob;
        }
    }

    private generateUniqueFilename(): string {
        const timestamp = Date.now();
        return 'Raptor' + '-' + timestamp;
    }
}
